// PlanePicker — компонент выбора плоскости для нового эскиза.
// Показывает 3 базовые плоскости XY/XZ/YZ как полупрозрачные квадраты
// с цветной обводкой. При наведении — подсвечиваются. При клике — создаётся
// эскиз и режим переключается на 'sketch' с этой плоскостью.

import React, { useMemo } from 'react';
import * as THREE from 'three';
import useCadStore from '../store/useCadStore';
import { workplaneBasis } from '../workplaneGeometry';

// Базовые плоскости origin-системы координат — это «затравка», они всегда
// под рукой. Размер маленький (40 мм), чтобы не мешать выбирать грани тел
// которые могут быть рядом.
const PLANE_SIZE = 40; // мм

// Quaternion для каждой плоскости — точно как у replicad PLANES_CONFIG.
// Подробности в SketchOverlay.planeQuaternion().
function planeQuat(planeId) {
    let xDir, yDir, zDir;
    if (planeId === 'XZ') {
        xDir = new THREE.Vector3(1, 0, 0);
        yDir = new THREE.Vector3(0, 0, 1);
        zDir = new THREE.Vector3(0, -1, 0);
    } else if (planeId === 'YZ') {
        xDir = new THREE.Vector3(0, 1, 0);
        yDir = new THREE.Vector3(0, 0, 1);
        zDir = new THREE.Vector3(1, 0, 0);
    } else {
        xDir = new THREE.Vector3(1, 0, 0);
        yDir = new THREE.Vector3(0, 1, 0);
        zDir = new THREE.Vector3(0, 0, 1);
    }
    const m = new THREE.Matrix4().makeBasis(xDir, yDir, zDir);
    return new THREE.Quaternion().setFromRotationMatrix(m);
}

const PLANES = [
    { id: 'XY', color: '#3a78ff', colorHover: '#5a98ff', label: 'Сверху (XY)' },
    { id: 'XZ', color: '#0c8',    colorHover: '#2da',    label: 'Спереди (XZ)' },
    { id: 'YZ', color: '#cc3344', colorHover: '#e85562', label: 'Справа (YZ)' },
];

// Мировая позиция плоскости со смещением — та же формула, что в
// SketchOverlay.planeWorldPosition (должны совпадать пиксель-в-пиксель).
function planeOffsetPosition(basePlane, offset) {
    if (!offset) return [0, 0, 0];
    if (basePlane === 'XZ') return [0, -offset, 0];
    if (basePlane === 'YZ') return [offset, 0, 0];
    return [0, 0, offset];  // XY
}

// Ориентация+позиция рабочей плоскости любого типа (offset/angle/midplane/custom).
function workplaneTransform(wp) {
    if (!wp.kind || wp.kind === 'offset') {
        return {
            quat: planeQuat(wp.basePlane),
            pos: planeOffsetPosition(wp.basePlane, wp.offset),
        };
    }
    const b = workplaneBasis(wp);
    if (!b) return { quat: planeQuat(wp.basePlane || 'XY'), pos: [0, 0, 0] };
    const xDir = new THREE.Vector3(...b.xDir).normalize();
    const zDir = new THREE.Vector3(...b.normal).normalize();
    const yDir = new THREE.Vector3().crossVectors(zDir, xDir).normalize();
    const m = new THREE.Matrix4().makeBasis(xDir, yDir, zDir);
    return {
        quat: new THREE.Quaternion().setFromRotationMatrix(m),
        pos: b.origin,
    };
}


const PlaneCard = ({ plane, hovered, onHover, onClick }) => {
    const quat = useMemo(() => planeQuat(plane.id), [plane.id]);
    return (
        <group quaternion={quat}>
            <mesh
                onPointerOver={(e) => { e.stopPropagation(); onHover(plane.id); }}
                onPointerOut={(e) => { e.stopPropagation(); onHover(null); }}
                onClick={(e) => { e.stopPropagation(); onClick(plane.id); }}
            >
                <planeGeometry args={[PLANE_SIZE, PLANE_SIZE]}/>
                <meshBasicMaterial
                    color={hovered ? plane.colorHover : plane.color}
                    transparent
                    opacity={hovered ? 0.45 : 0.18}
                    side={THREE.DoubleSide}
                    depthWrite={false}
                />
            </mesh>
            <lineSegments>
                <edgesGeometry args={[new THREE.PlaneGeometry(PLANE_SIZE, PLANE_SIZE)]}/>
                <lineBasicMaterial
                    color={hovered ? plane.colorHover : plane.color}
                    linewidth={2}
                    transparent
                    opacity={hovered ? 1 : 0.85}
                />
            </lineSegments>
        </group>
    );
};


// Размер рабочей плоскости — крупнее базовых, это полноценная
// рабочая зона для эскиза.
const WP_SIZE = 80; // мм

// Карточка рабочей плоскости со смещением. Оранжевая (цвет construct-
// группы). При клике onPick получает объект workplane.
const WorkplaneCard = ({ wp, hovered, onHover, onClick }) => {
    const { quat, pos } = useMemo(() => workplaneTransform(wp), [wp]);
    const color = hovered ? '#ffae5c' : '#e88a3a';
    return (
        <group quaternion={quat} position={pos}>
            <mesh
                onPointerOver={(e) => { e.stopPropagation(); onHover(wp.id); }}
                onPointerOut={(e) => { e.stopPropagation(); onHover(null); }}
                onClick={(e) => {
                    e.stopPropagation();
                    onClick({
                        kind: 'workplane',
                        workplaneId: wp.id,
                        plane: wp.basePlane,
                        offset: wp.offset,
                    });
                }}
            >
                <planeGeometry args={[WP_SIZE, WP_SIZE]}/>
                <meshBasicMaterial
                    color={color}
                    transparent
                    opacity={hovered ? 0.4 : 0.16}
                    side={THREE.DoubleSide}
                    depthWrite={false}
                />
            </mesh>
            <lineSegments>
                <edgesGeometry args={[new THREE.PlaneGeometry(WP_SIZE, WP_SIZE)]}/>
                <lineBasicMaterial
                    color={color}
                    linewidth={2}
                    transparent
                    opacity={hovered ? 1 : 0.8}
                />
            </lineSegments>
        </group>
    );
};


/**
 * 3D-компонент. Используется внутри <Canvas/> r3f-сцены.
 * Появляется когда mode === 'select_plane'.
 * Рисует 3 базовые плоскости + все рабочие плоскости со смещением.
 */
const PlanePicker = ({ onPick }) => {
    const hoveredPlane = useCadStore((s) => s.hoveredPlane);
    const setHoveredPlane = useCadStore((s) => s.setHoveredPlane);
    const workplanes = useCadStore((s) => s.doc.workplanes || []);
    // hover рабочих плоскостей — локальный (hoveredPlane в store
    // строковый, заточен под базовые XY/XZ/YZ).
    const [hoveredWp, setHoveredWp] = React.useState(null);

    return (
        <group>
            {PLANES.map((p) => (
                <PlaneCard
                    key={p.id}
                    plane={p}
                    hovered={hoveredPlane === p.id}
                    onHover={setHoveredPlane}
                    onClick={onPick}
                />
            ))}
            {workplanes.map((wp) => (
                <WorkplaneCard
                    key={wp.id}
                    wp={wp}
                    hovered={hoveredWp === wp.id}
                    onHover={setHoveredWp}
                    onClick={onPick}
                />
            ))}
        </group>
    );
};


// Линия рабочей оси — продлеваем отрезок p1→p2 в обе стороны, чтобы
// ось выглядела как бесконечная (как принято в CAD).
const WorkaxisLine = ({ ax }) => {
    const geometry = useMemo(() => {
        const p1 = ax.p1, p2 = ax.p2;
        const dx = p2[0] - p1[0], dy = p2[1] - p1[1], dz = p2[2] - p1[2];
        const len = Math.hypot(dx, dy, dz) || 1;
        // Единичное направление, продлеваем на 60 мм за каждую точку.
        const ux = dx / len, uy = dy / len, uz = dz / len;
        const ext = 60;
        const a = [p1[0] - ux * ext, p1[1] - uy * ext, p1[2] - uz * ext];
        const b = [p2[0] + ux * ext, p2[1] + uy * ext, p2[2] + uz * ext];
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(
            new Float32Array([...a, ...b]), 3));
        return g;
    }, [ax.p1, ax.p2]);
    return (
        <line geometry={geometry}>
            <lineBasicMaterial color="#e8b73a" linewidth={2}/>
        </line>
    );
};


// Пассивная отрисовка рабочей геометрии (плоскостей и осей) в обычном
// view3d — чтобы их было видно в сцене (без интерактивности).
const WorkplanesDisplay = () => {
    const workplanes = useCadStore((s) => s.doc.workplanes || []);
    const workaxes = useCadStore((s) => s.doc.workaxes || []);
    if (workplanes.length === 0 && workaxes.length === 0) return null;
    return (
        <group>
            {workplanes.map((wp) => {
                const { quat, pos } = workplaneTransform(wp);
                return (
                    <group key={wp.id} quaternion={quat} position={pos}>
                        <mesh>
                            <planeGeometry args={[WP_SIZE, WP_SIZE]}/>
                            <meshBasicMaterial
                                color="#e88a3a"
                                transparent
                                opacity={0.08}
                                side={THREE.DoubleSide}
                                depthWrite={false}
                            />
                        </mesh>
                        <lineSegments>
                            <edgesGeometry args={[new THREE.PlaneGeometry(WP_SIZE, WP_SIZE)]}/>
                            <lineBasicMaterial
                                color="#e88a3a"
                                transparent
                                opacity={0.5}
                            />
                        </lineSegments>
                    </group>
                );
            })}
            {workaxes.map((ax) => (
                <WorkaxisLine key={ax.id} ax={ax}/>
            ))}
        </group>
    );
};

export default PlanePicker;
export { PLANES, WorkplanesDisplay };
